export interface IndicatorRecordItem {
  id: string;
  title: string;
  category: string;
  severity?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'LEVEL 1' | 'LEVEL 2' | 'RED' | 'AMBER' | 'INFO';
  status: string;
  statusBadgeVariant: 'red' | 'amber' | 'blue' | 'green' | 'purple' | 'gray';
  timeInfo: string;
  assignedTo?: string;
  targetTab: string;
  targetId?: string;
  summary?: string;
}

export interface IndicatorModalState {
  cardTitle: string;
  filterLabel: string;
  dotColorClass: string;
  subtitle: string;
  targetTab: string;
  records: IndicatorRecordItem[];
}

// 1. MY REPORTS INDICATORS
export const OVERDUE_REPORTS_DATA: IndicatorRecordItem[] = [
  {
    id: 'OCC-2026-0138',
    title: 'Tailstrike Warning during Rotation (RWY 35L)',
    category: 'Flight Operations',
    severity: 'CRITICAL',
    status: '2 DAYS OVERDUE',
    statusBadgeVariant: 'red',
    timeInfo: 'Due: 24 Oct 2026',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'occurrences',
    targetId: 'OCC-2026-0138',
    summary: 'Pitch rate warning triggered at rotation on aircraft TC-JHV. EASA Part-ORO regulatory reporting deadline exceeded.'
  },
  {
    id: 'REP-2026-0298',
    title: 'Hydraulic B-System Pressure Drop on Final Descent',
    category: 'Airworthiness / Maintenance',
    severity: 'HIGH',
    status: '5 DAYS OVERDUE',
    statusBadgeVariant: 'red',
    timeInfo: 'Due: 21 Oct 2026',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'occurrences',
    targetId: 'REP-2026-0298',
    summary: 'Hydraulic pressure drop below 1800 PSI on B-system. Maintenance rectification report overdue from line station.'
  },
  {
    id: 'HAZ-2026-0064',
    title: 'Runway 05 FOD Accumulation and Construction Debris',
    category: 'Ground Safety',
    severity: 'MEDIUM',
    status: '1 DAY OVERDUE',
    statusBadgeVariant: 'red',
    timeInfo: 'Due: 25 Oct 2026',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'hazards',
    targetId: 'HAZ-2026-0064',
    summary: 'Civil works near Taxiway Golf resulted in concrete aggregate dispersal. Sweeper verification report required.'
  }
];

export const IN_PROGRESS_REPORTS_DATA: IndicatorRecordItem[] = [
  {
    id: 'OCC-2026-0142',
    title: 'Bird Strike during Initial Climb (LTFM RWY 35L)',
    category: 'Flight Operations',
    severity: 'HIGH',
    status: 'IN PROGRESS',
    statusBadgeVariant: 'blue',
    timeInfo: 'Due: 28 Oct 2026 (2 days left)',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'occurrences',
    targetId: 'OCC-2026-0142',
    summary: 'Bird impact on No. 1 engine cowl during climb passing 1500ft. DFDR readout downloaded, awaiting boroscope inspection.'
  },
  {
    id: 'HAZ-2026-0087',
    title: 'Taxiway Echo Ice Accumulation Risk',
    category: 'Ground Operations',
    severity: 'MEDIUM',
    status: 'IN PROGRESS',
    statusBadgeVariant: 'blue',
    timeInfo: 'Due: 30 Oct 2026 (4 days left)',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'hazards',
    targetId: 'HAZ-2026-0087',
    summary: 'Inadequate anti-icing liquid runoff along high-speed turnoff. Runway de-icing coordinator conducting test sweeps.'
  },
  {
    id: 'INV-2026-0045',
    title: 'Unstabilized Approach Investigation at LTFJ',
    category: 'Safety Office',
    severity: 'HIGH',
    status: 'IN PROGRESS',
    statusBadgeVariant: 'blue',
    timeInfo: 'Due: 05 Nov 2026 (10 days left)',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'investigations',
    targetId: 'INV-2026-0045',
    summary: 'Speed high by 18 knots at 500ft IMC. Root cause 5-Why analysis undergoing safety committee review.'
  },
  {
    id: 'REP-2026-0312',
    title: 'Emergency Evacuation Slide Pressure Gauge Anomaly',
    category: 'Cabin Safety',
    severity: 'MEDIUM',
    status: 'IN PROGRESS',
    statusBadgeVariant: 'blue',
    timeInfo: 'Due: 07 Nov 2026 (12 days left)',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'occurrences',
    targetId: 'REP-2026-0312',
    summary: 'Pre-flight check found Door 2R slide pack gauge showing amber marginal zone. Component quarantined for bench test.'
  },
  {
    id: 'ACT-2026-0119',
    title: 'Runway Incursion Corrective Action Plan',
    category: 'Compliance / Safety Actions',
    severity: 'MEDIUM',
    status: 'IN PROGRESS',
    statusBadgeVariant: 'blue',
    timeInfo: 'Due: 10 Nov 2026 (15 days left)',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'actions',
    targetId: 'ACT-2026-0119',
    summary: 'Re-training syllabus on vehicle transponder activation rules before entering active runway protection zones.'
  }
];

export const PENDING_REPORTS_DATA: IndicatorRecordItem[] = [
  {
    id: 'REP-2026-0321',
    title: 'Cabin Fume Event Report during Descent',
    category: 'Flight Operations',
    severity: 'MEDIUM',
    status: 'PENDING ACTION',
    statusBadgeVariant: 'amber',
    timeInfo: 'Due: 02 Nov 2026',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'ingestion',
    targetId: 'REP-2026-0321',
    summary: 'Mild electrical ozone smell in aft galley. APU bleed air duct inspection ordered; awaiting technical log confirmation.'
  },
  {
    id: 'REP-2026-0340',
    title: 'Line Station De-icing Equipment Calibration Expired',
    category: 'Ground Safety',
    severity: 'HIGH',
    status: 'PENDING TRIAGE',
    statusBadgeVariant: 'amber',
    timeInfo: 'Due: 04 Nov 2026',
    assignedTo: 'Line Station Manager',
    targetTab: 'ingestion',
    targetId: 'REP-2026-0340',
    summary: 'Refractometer calibration certificate expired 3 days prior. De-icing rig temporarily suspended from operation.'
  },
  {
    id: 'HAZ-2026-0091',
    title: 'Apron Ramp Floodlighting Defect at Stand 204',
    category: 'Airfield Operations',
    severity: 'LOW',
    status: 'PENDING ASSIGNMENT',
    statusBadgeVariant: 'gray',
    timeInfo: 'Due: 08 Nov 2026',
    assignedTo: 'Unassigned',
    targetTab: 'hazards',
    targetId: 'HAZ-2026-0091',
    summary: 'High-mast LED array flickering during pushback operations creating shadows around wing walker view.'
  },
  {
    id: 'ACT-2026-0105',
    title: 'Calibration Tooling Corrective Verification',
    category: 'Maintenance QMS',
    severity: 'LOW',
    status: 'PENDING CLOSURE',
    statusBadgeVariant: 'gray',
    timeInfo: 'Due: 14 Nov 2026',
    assignedTo: 'Quality Inspector',
    targetTab: 'actions',
    targetId: 'ACT-2026-0105',
    summary: 'Tool control room logs updated with automated RFID verification system. Awaiting final QA manager sign-off.'
  }
];

// 2. WAITING ASSESSMENT INDICATORS
export const WAITING_ASSESS_HIGH_DATA: IndicatorRecordItem[] = [
  {
    id: 'REP-2026-0438',
    title: 'Hard Landing Event (G-load 2.1g) - TC-JHV',
    category: 'Occurrence Report',
    severity: 'CRITICAL',
    status: 'AWAITING TRIAGE',
    statusBadgeVariant: 'red',
    timeInfo: 'Received 2 hours ago',
    assignedTo: 'SMS Intake Queue',
    targetTab: 'ingestion',
    targetId: 'REP-2026-0438',
    summary: 'Vertical acceleration exceeded manufacturer structural inspection limit (2.1g). Mandatory 72-hour EASA Annex 13 notice.'
  },
  {
    id: 'REP-2026-0437',
    title: 'Severe Cabin Turbulence and Cabin Crew Injury',
    category: 'Occurrence Report',
    severity: 'HIGH',
    status: 'AWAITING TRIAGE',
    statusBadgeVariant: 'red',
    timeInfo: 'Received 4 hours ago',
    assignedTo: 'SMS Intake Queue',
    targetTab: 'ingestion',
    targetId: 'REP-2026-0437',
    summary: 'Flight TK182 encountered clear air turbulence at FL340. One cabin crew member treated for ankle sprain.'
  },
  {
    id: 'REP-2026-0436',
    title: 'Wildlife Sighting (Stork Flock) on Runway during Flare',
    category: 'Hazard Report',
    severity: 'HIGH',
    status: 'AWAITING TRIAGE',
    statusBadgeVariant: 'red',
    timeInfo: 'Received 5 hours ago',
    assignedTo: 'SMS Intake Queue',
    targetTab: 'ingestion',
    targetId: 'REP-2026-0436',
    summary: 'Crew initiated go-around due to flock of storks crossing the threshold at 50ft AGL.'
  }
];

export const WAITING_ASSESS_MED_DATA: IndicatorRecordItem[] = [
  {
    id: 'REP-2026-0435',
    title: 'Technical Defect - Hydraulic Leak Yellow System',
    category: 'Occurrence Report',
    severity: 'MEDIUM',
    status: 'AWAITING TRIAGE',
    statusBadgeVariant: 'amber',
    timeInfo: 'Received 6 hours ago',
    assignedTo: 'SMS Intake Queue',
    targetTab: 'ingestion',
    targetId: 'REP-2026-0435',
    summary: 'Yellow hydraulic reservoir quantity dropped to 45% during taxi to gate. No flight control anomalies noted.'
  },
  {
    id: 'REP-2026-0434',
    title: 'FOD (Metal Bracket) Found on Apron Stand 112',
    category: 'Hazard Report',
    severity: 'MEDIUM',
    status: 'AWAITING TRIAGE',
    statusBadgeVariant: 'amber',
    timeInfo: 'Received 7 hours ago',
    assignedTo: 'SMS Intake Queue',
    targetTab: 'ingestion',
    targetId: 'REP-2026-0434',
    summary: 'Aircraft tug ground crew spotted a 15cm sheared bracket near main gear position before docking.'
  },
  {
    id: 'REP-2026-0432',
    title: 'Ground Power Unit Cable Insulation Wear',
    category: 'Hazard Report',
    severity: 'MEDIUM',
    status: 'AWAITING TRIAGE',
    statusBadgeVariant: 'amber',
    timeInfo: 'Received 12 hours ago',
    assignedTo: 'SMS Intake Queue',
    targetTab: 'ingestion',
    targetId: 'REP-2026-0432',
    summary: 'GPU 400Hz cable outer sheath abraded down to inner protective mesh. Cable removed from service.'
  }
];

export const WAITING_ASSESS_LOW_DATA: IndicatorRecordItem[] = [
  {
    id: 'REP-2026-0430',
    title: 'Passenger Boarding Gate Scanner Intermittent Lag',
    category: 'Operations Report',
    severity: 'LOW',
    status: 'AWAITING TRIAGE',
    statusBadgeVariant: 'green',
    timeInfo: 'Received 1 day ago',
    assignedTo: 'Ground Operations',
    targetTab: 'ingestion',
    targetId: 'REP-2026-0430',
    summary: 'Gate 201 e-gate scanners experienced 4-second delay during batch boarding. IT service ticket logged.'
  },
  {
    id: 'REP-2026-0428',
    title: 'Minor Paint Scuff on Baggage Belt Shroud',
    category: 'Maintenance Report',
    severity: 'LOW',
    status: 'AWAITING TRIAGE',
    statusBadgeVariant: 'green',
    timeInfo: 'Received 2 days ago',
    assignedTo: 'Ground Equipment',
    targetTab: 'ingestion',
    targetId: 'REP-2026-0428',
    summary: 'Cosmetic abrasion on mobile belt loader bumper. Rubber bumper intact, no structural damage.'
  }
];

// 3. NEW FEED UPDATES INDICATORS
export const FEED_UPDATES_UNREAD_DATA: IndicatorRecordItem[] = [
  {
    id: 'FEED-01',
    title: 'RCA 5-Why Analysis Updated for OCC-2026-0142',
    category: 'Safety Investigation',
    severity: 'HIGH',
    status: 'UNREAD',
    statusBadgeVariant: 'purple',
    timeInfo: '10 minutes ago',
    assignedTo: 'Capt. S. Yilmaz',
    targetTab: 'investigations',
    targetId: 'OCC-2026-0142',
    summary: 'Investigator updated contributory root causes regarding airport bird scaring device deployment intervals.'
  },
  {
    id: 'FEED-02',
    title: 'Maintenance Log Attachment Added to REP-2026-0438',
    category: 'Tech Log Ingestion',
    severity: 'HIGH',
    status: 'UNREAD',
    statusBadgeVariant: 'purple',
    timeInfo: '25 minutes ago',
    assignedTo: 'M. Demir (Maintenance)',
    targetTab: 'occurrences',
    targetId: 'REP-2026-0438',
    summary: 'Aircraft technical log page 44810 uploaded showing landing gear shock strut pressure test results.'
  },
  {
    id: 'FEED-03',
    title: 'MOC-2026-0018 Pending Safety Review Sign-Off',
    category: 'Management of Change',
    severity: 'HIGH',
    status: 'UNREAD',
    statusBadgeVariant: 'purple',
    timeInfo: '1 hour ago',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'moc',
    targetId: 'MOC-2026-0018',
    summary: 'Revised de-icing procedure change package ready for Safety Manager operational sign-off.'
  },
  {
    id: 'FEED-04',
    title: 'CAPA-2026-0041 Reassigned to Flight Ops Lead',
    category: 'Safety Actions',
    severity: 'MEDIUM',
    status: 'UNREAD',
    statusBadgeVariant: 'purple',
    timeInfo: '2 hours ago',
    assignedTo: 'Capt. E. Kaya',
    targetTab: 'actions',
    targetId: 'CAPA-2026-0041',
    summary: 'Anti-ice SOP revision reassigned for simulator validation and flight crew briefing bulletin preparation.'
  },
  {
    id: 'FEED-05',
    title: 'Internal Audit AUD-2026-0018 Checklist Published',
    category: 'Quality Audits',
    severity: 'MEDIUM',
    status: 'UNREAD',
    statusBadgeVariant: 'purple',
    timeInfo: '3 hours ago',
    assignedTo: 'K. Anderson (Lead)',
    targetTab: 'audits',
    targetId: 'AUD-2026-0018',
    summary: 'Ramp Safety & Ground Handling audit checklist finalized. 48 inspection checkpoints active.'
  },
  {
    id: 'FEED-06',
    title: 'Runway Friction Index Measurement Updated (LTBA)',
    category: 'Airfield Safety',
    severity: 'LOW',
    status: 'UNREAD',
    statusBadgeVariant: 'purple',
    timeInfo: '4 hours ago',
    assignedTo: 'Airfield Duty Office',
    targetTab: 'hazards',
    targetId: 'HAZ-2026-0087',
    summary: 'Continuous friction measurement vehicle recorded average Mu of 0.52 across runway 17L.'
  },
  {
    id: 'FEED-07',
    title: 'Hazard HAZ-2026-0087 Initial Risk Rating Confirmed',
    category: 'Risk Management',
    severity: 'MEDIUM',
    status: 'UNREAD',
    statusBadgeVariant: 'purple',
    timeInfo: '5 hours ago',
    assignedTo: 'Safety Office',
    targetTab: 'hazards',
    targetId: 'HAZ-2026-0087',
    summary: 'Risk scoring evaluated at 3C (Tolerable with mitigation). Monitoring task initiated.'
  },
  {
    id: 'FEED-08',
    title: 'Safety Policy Rev 4.1 Document Approved',
    category: 'Document Control',
    severity: 'LOW',
    status: 'UNREAD',
    statusBadgeVariant: 'purple',
    timeInfo: '6 hours ago',
    assignedTo: 'Accountable Executive',
    targetTab: 'documents',
    targetId: 'DOC-2026-0012',
    summary: 'Corporate Aviation Safety Policy Revision 4.1 signed and published to Document Management Portal.'
  }
];

export const FEED_UPDATES_READ_DATA: IndicatorRecordItem[] = [
  {
    id: 'FEED-ARCH-01',
    title: 'Investigation INV-2026-0039 Draft Closed for Signatures',
    category: 'Safety Investigation',
    severity: 'HIGH',
    status: 'READ',
    statusBadgeVariant: 'gray',
    timeInfo: 'Yesterday at 16:45',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'investigations',
    targetId: 'INV-2026-0039',
    summary: 'Draft investigation report submitted to Safety Review Board for formal ratification.'
  },
  {
    id: 'FEED-ARCH-02',
    title: 'EASA CAMO Audit Preparation Evidence Package Uploaded',
    category: 'Audits & Oversight',
    severity: 'HIGH',
    status: 'READ',
    statusBadgeVariant: 'gray',
    timeInfo: 'Yesterday at 14:20',
    assignedTo: 'QA Board',
    targetTab: 'audits',
    targetId: 'AUD-2026-0019',
    summary: 'Maintenance oversight documentation bundle compiled and indexed for upcoming regulatory review.'
  },
  {
    id: 'FEED-ARCH-03',
    title: 'Fuel Storage Line Station Inspection Confirmed',
    category: 'Ramp Inspection',
    severity: 'MEDIUM',
    status: 'READ',
    statusBadgeVariant: 'gray',
    timeInfo: '2 days ago',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'audits',
    targetId: 'INS-2026-0092',
    summary: 'Date confirmed with airport fuel farm operator for 08 Nov 2026.'
  }
];

// 4. OVERDUE ACTIONS INDICATORS
export const OVERDUE_ACTIONS_SAFETY_DATA: IndicatorRecordItem[] = [
  {
    id: 'CAPA-2026-0041',
    title: 'Update Engine Anti-Ice Pre-Departure Checklist SOP',
    category: 'Flight Safety CAPA',
    severity: 'CRITICAL',
    status: '4 DAYS OVERDUE',
    statusBadgeVariant: 'red',
    timeInfo: 'Due: 22 Oct 2026',
    assignedTo: 'Capt. E. Kaya (Flight Ops)',
    targetTab: 'actions',
    targetId: 'CAPA-2026-0041',
    summary: 'Mandatory revision to OM-B cold weather operations section following ground icing precursor event.'
  },
  {
    id: 'CAPA-2026-0038',
    title: 'Ramp Marshalling Distance Sensor Recalibration',
    category: 'Ground Safety CAPA',
    severity: 'HIGH',
    status: '9 DAYS OVERDUE',
    statusBadgeVariant: 'red',
    timeInfo: 'Due: 17 Oct 2026',
    assignedTo: 'M. Arslan (Ground Support)',
    targetTab: 'actions',
    targetId: 'CAPA-2026-0038',
    summary: 'Ultrasonic distance sensors on stands 101-108 require calibration verification after software update.'
  }
];

export const OVERDUE_ACTIONS_INVS_DATA: IndicatorRecordItem[] = [
  {
    id: 'INV-2026-0032',
    title: 'TCAS Resolution Advisory In-Flight Encounter',
    category: 'Flight Safety Investigation',
    severity: 'HIGH',
    status: '3 DAYS OVERDUE',
    statusBadgeVariant: 'red',
    timeInfo: 'Due: 23 Oct 2026',
    assignedTo: 'Capt. S. Yilmaz',
    targetTab: 'investigations',
    targetId: 'INV-2026-0032',
    summary: '30-day statutory preliminary report to DGCA exceeded. Radar replay analysis pending ATC data upload.'
  },
  {
    id: 'INV-2026-0035',
    title: 'Ramp Belt Loader Equipment Scrape on A321 Cargo Door',
    category: 'Ground Operations',
    severity: 'MEDIUM',
    status: '6 DAYS OVERDUE',
    statusBadgeVariant: 'red',
    timeInfo: 'Due: 20 Oct 2026',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'investigations',
    targetId: 'INV-2026-0035',
    summary: 'Investigation into operator clearance procedures. Interview with ground handler dispatch supervisor pending.'
  },
  {
    id: 'INV-2026-0028',
    title: 'B737 Nosewheel Steering Shimmy Anomaly on Landing',
    category: 'Airworthiness Investigation',
    severity: 'HIGH',
    status: '11 DAYS OVERDUE',
    statusBadgeVariant: 'red',
    timeInfo: 'Due: 15 Oct 2026',
    assignedTo: 'T. Kaya (Engineering)',
    targetTab: 'investigations',
    targetId: 'INV-2026-0028',
    summary: 'Severe vibration during rollout. Torque link bushing wear measurements received from shop.'
  },
  {
    id: 'INV-2026-0024',
    title: 'ATC Communication Lost Handover Over Black Sea',
    category: 'Flight Operations',
    severity: 'MEDIUM',
    status: '15 DAYS OVERDUE',
    statusBadgeVariant: 'red',
    timeInfo: 'Due: 11 Oct 2026',
    assignedTo: 'S. Demir (Avionics)',
    targetTab: 'investigations',
    targetId: 'INV-2026-0024',
    summary: '12-minute VHF silence during sector transition. HF backup radio sensitivity log requested from maintenance.'
  }
];

// 5. HIGH & CRITICAL RISKS INDICATORS
export const CRITICAL_RISKS_RED_DATA: IndicatorRecordItem[] = [
  {
    id: 'RSK-2026-0012',
    title: 'Unstabilized Approaches in High Crosswind at LTFJ RWY 06',
    category: 'Flight Safety Risk',
    severity: 'CRITICAL',
    status: '5A (UNACCEPTABLE)',
    statusBadgeVariant: 'red',
    timeInfo: 'Next Review: 29 Oct 2026',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'risks',
    targetId: 'RSK-2026-0012',
    summary: 'Recurrent gusty crosswind exceedances. Mandatory go-around policy and simulator training package under review.'
  }
];

export const CRITICAL_RISKS_AMBER_DATA: IndicatorRecordItem[] = [
  {
    id: 'RSK-2026-0019',
    title: 'Ground Handling Vehicle Collisions During Fast Turnaround',
    category: 'Ramp Operations Risk',
    severity: 'HIGH',
    status: '3C (TOLERABLE)',
    statusBadgeVariant: 'amber',
    timeInfo: 'Next Review: 15 Nov 2026',
    assignedTo: 'Ground Safety Office',
    targetTab: 'risks',
    targetId: 'RSK-2026-0019',
    summary: 'High apron traffic congestion between catering and baggage vehicles. Dedicated driving lanes proposed.'
  },
  {
    id: 'RSK-2026-0022',
    title: 'Cabin Crew Fatigue on Night Multi-Sector Rosters',
    category: 'Fatigue Risk Management',
    severity: 'HIGH',
    status: '3B (TOLERABLE)',
    statusBadgeVariant: 'amber',
    timeInfo: 'Next Review: 20 Nov 2026',
    assignedTo: 'FRMS Safety Board',
    targetTab: 'risks',
    targetId: 'RSK-2026-0022',
    summary: 'Bio-mathematical fatigue score peaks on 4th consecutive night sector. Roster pattern adjustment under evaluation.'
  }
];

// 6. OPEN INVESTIGATIONS INDICATORS
export const OPEN_INVS_INQUIRY_DATA: IndicatorRecordItem[] = [
  {
    id: 'INV-2026-0042',
    title: 'Runway Excursion Precursor (Wet Runway Braking Action)',
    category: 'Flight Safety',
    severity: 'HIGH',
    status: 'INQUIRY PHASE',
    statusBadgeVariant: 'blue',
    timeInfo: 'Target: 12 Nov 2026',
    assignedTo: 'Capt. S. Yilmaz',
    targetTab: 'investigations',
    targetId: 'INV-2026-0042',
    summary: 'Evidence collection phase: Flight data recorder telemetry, runway grooving condition records and ATC transcripts.'
  },
  {
    id: 'INV-2026-0044',
    title: 'Airspeed Discrepancy After Takeoff Pitot Heat Indication',
    category: 'Avionics Safety',
    severity: 'MEDIUM',
    status: 'INQUIRY PHASE',
    statusBadgeVariant: 'blue',
    timeInfo: 'Target: 16 Nov 2026',
    assignedTo: 'M. Arslan',
    targetTab: 'investigations',
    targetId: 'INV-2026-0044',
    summary: 'Pilot reports 25kt discrepancy between Captain and First Officer airspeed indicators climbing through FL100.'
  }
];

export const OPEN_INVS_RCA_DATA: IndicatorRecordItem[] = [
  {
    id: 'INV-2026-0045',
    title: 'Unstabilized Approach Investigation at LTFJ',
    category: 'Safety Office',
    severity: 'HIGH',
    status: 'RCA (5-WHY)',
    statusBadgeVariant: 'amber',
    timeInfo: 'Target: 05 Nov 2026',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'investigations',
    targetId: 'INV-2026-0045',
    summary: 'Investigating human factors, ATC descent instructions and energy management profile on final approach.'
  },
  {
    id: 'INV-2026-0040',
    title: 'Hydraulic Yellow System Overheat on Taxi-in',
    category: 'Airworthiness',
    severity: 'MEDIUM',
    status: 'ISHIKAWA RCA',
    statusBadgeVariant: 'amber',
    timeInfo: 'Target: 09 Nov 2026',
    assignedTo: 'T. Kaya',
    targetTab: 'investigations',
    targetId: 'INV-2026-0040',
    summary: 'Cause-and-effect diagram analyzing electric pump continuous running, valve contamination, and thermal sensor calibration.'
  }
];

export const OPEN_INVS_FINAL_DATA: IndicatorRecordItem[] = [
  {
    id: 'INV-2026-0039',
    title: 'Engine Oil Pressure Drop In-flight (TC-JHV)',
    category: 'Flight Safety',
    severity: 'HIGH',
    status: 'FINAL REVIEW',
    statusBadgeVariant: 'green',
    timeInfo: 'Target: 18 Nov 2026',
    assignedTo: 'M. Demir',
    targetTab: 'investigations',
    targetId: 'INV-2026-0039',
    summary: 'Draft report finalized: Defective pressure transmitter sensor replaced. Awaiting Safety Board ratification.'
  }
];

// 7. QUALITY FINDINGS INDICATORS
export const QUALITY_FINDINGS_L1_DATA: IndicatorRecordItem[] = [
  {
    id: 'FND-2026-0008',
    title: 'Non-calibrated Torque Wrench used on Landing Gear Assembly',
    category: 'EASA Part-145 Audit',
    severity: 'LEVEL 1',
    status: 'LEVEL 1 NON-CONFORMANCE',
    statusBadgeVariant: 'red',
    timeInfo: 'Rectification Due: 29 Oct 2026',
    assignedTo: 'Quality Assurance Board',
    targetTab: 'findings',
    targetId: 'FND-2026-0008',
    summary: 'Critical tooling calibration interval exceeded by 14 days during nose gear trunnion overhaul. Immediate quarantine enforced.'
  }
];

export const QUALITY_FINDINGS_L2_DATA: IndicatorRecordItem[] = [
  {
    id: 'FND-2026-0014',
    title: 'Line Maintenance Tech Log Book Stamp Legibility',
    category: 'Compliance Audit',
    severity: 'LEVEL 2',
    status: 'LEVEL 2 NON-CONFORMANCE',
    statusBadgeVariant: 'amber',
    timeInfo: 'Corrective Plan Due: 12 Nov 2026',
    assignedTo: 'K. Anderson (Lead)',
    targetTab: 'findings',
    targetId: 'FND-2026-0014',
    summary: 'Authorization stamp ink faint on 3 CRS release entries. Replacement digital stamp pad issued to line station.'
  },
  {
    id: 'FND-2026-0019',
    title: 'Dangerous Goods Packaging Labeling Minor Discrepancy',
    category: 'Cargo Audit',
    severity: 'LEVEL 2',
    status: 'LEVEL 2 NON-CONFORMANCE',
    statusBadgeVariant: 'amber',
    timeInfo: 'Corrective Plan Due: 18 Nov 2026',
    assignedTo: 'QA Cargo Unit',
    targetTab: 'findings',
    targetId: 'FND-2026-0019',
    summary: 'Secondary orientation label placement on Class 9 lithium battery package was partially obscured by adhesive tape.'
  },
  {
    id: 'FND-2026-0021',
    title: 'First Aid Kit Seal Inspection Interval Exceeded by 3 Days',
    category: 'Cabin Safety Audit',
    severity: 'LEVEL 2',
    status: 'LEVEL 2 NON-CONFORMANCE',
    statusBadgeVariant: 'amber',
    timeInfo: 'Corrective Plan Due: 25 Nov 2026',
    assignedTo: 'Cabin Safety Inspector',
    targetTab: 'findings',
    targetId: 'FND-2026-0021',
    summary: 'Aft cabin first aid kit inspection tag was not renewed on scheduled monthly A-check turnaround.'
  }
];

// 8. COMPLIANCE SCORE INDICATORS
export const COMPLIANCE_EASA_DATA: IndicatorRecordItem[] = [
  {
    id: 'COMP-EASA-01',
    title: 'EASA Part-ORO Management System Oversight',
    category: 'Regulatory Oversight',
    severity: 'INFO',
    status: '100% COMPLIANT',
    statusBadgeVariant: 'green',
    timeInfo: 'Audited: 15 Oct 2026',
    assignedTo: 'Compliance Monitoring Unit',
    targetTab: 'monitoring',
    targetId: 'COMP-EASA-01',
    summary: 'Full compliance with EASA Part-ORO.GEN.200 SMS documentation and management evaluation requirements.'
  },
  {
    id: 'COMP-EASA-02',
    title: 'EASA Part-145 Maintenance Oversight Compliance',
    category: 'Continuous Airworthiness',
    severity: 'INFO',
    status: '97.4% COMPLIANT',
    statusBadgeVariant: 'blue',
    timeInfo: 'Audited: 20 Oct 2026',
    assignedTo: 'CAMO QA Unit',
    targetTab: 'monitoring',
    targetId: 'COMP-EASA-02',
    summary: 'Minor observation on tool storage tracking. Corrective action in progress with line maintenance stations.'
  },
  {
    id: 'COMP-EASA-03',
    title: 'EASA Part-CAT Commercial Air Transport Standards',
    category: 'Flight Operations',
    severity: 'INFO',
    status: '98.8% COMPLIANT',
    statusBadgeVariant: 'green',
    timeInfo: 'Audited: 22 Oct 2026',
    assignedTo: 'Chief Pilot Safety',
    targetTab: 'monitoring',
    targetId: 'COMP-EASA-03',
    summary: 'Fuel planning, alternate aerodrome selection and performance calculations meeting all Part-CAT standards.'
  }
];

export const COMPLIANCE_ICAO_DATA: IndicatorRecordItem[] = [
  {
    id: 'COMP-ICAO-01',
    title: 'ICAO Annex 19 Safety Management System Framework',
    category: 'Safety Assurance',
    severity: 'INFO',
    status: '98.5% COMPLIANT',
    statusBadgeVariant: 'blue',
    timeInfo: 'Audited: 18 Oct 2026',
    assignedTo: 'Safety Assurance Board',
    targetTab: 'monitoring',
    targetId: 'COMP-ICAO-01',
    summary: 'All 4 components and 12 elements of ICAO Doc 9859 Safety Management Manual successfully verified.'
  },
  {
    id: 'COMP-ICAO-02',
    title: 'ICAO Annex 14 Aerodromes & Ramp Ground Operations',
    category: 'Aerodrome Standards',
    severity: 'INFO',
    status: '97.1% COMPLIANT',
    statusBadgeVariant: 'blue',
    timeInfo: 'Audited: 21 Oct 2026',
    assignedTo: 'Airfield Operations Lead',
    targetTab: 'monitoring',
    targetId: 'COMP-ICAO-02',
    summary: 'Ground equipment parking clearances and stand marking conformance verified across base hubs.'
  }
];

// 9. MOC CHANGES INDICATORS
export const MOC_IMPACT_DATA: IndicatorRecordItem[] = [
  {
    id: 'MOC-2026-0019',
    title: 'Transition to Electronic Flight Bag (EFB) Version 4.2',
    category: 'Flight Operations Change',
    severity: 'HIGH',
    status: 'IMPACT ASSESSMENT',
    statusBadgeVariant: 'blue',
    timeInfo: 'Target: 10 Nov 2026',
    assignedTo: 'Capt. S. Yilmaz',
    targetTab: 'moc',
    targetId: 'MOC-2026-0019',
    summary: 'Formal risk and human factors assessment for upgraded performance calculation software on pilot iPads.'
  }
];

export const MOC_APPROVAL_DATA: IndicatorRecordItem[] = [
  {
    id: 'MOC-2026-0018',
    title: 'Implementation of Revised De-icing Procedures at LTFM',
    category: 'Ground Safety Change',
    severity: 'HIGH',
    status: 'PENDING APPROVAL',
    statusBadgeVariant: 'amber',
    timeInfo: 'Target: 30 Oct 2026',
    assignedTo: 'Erdal Aydeniz',
    targetTab: 'moc',
    targetId: 'MOC-2026-0018',
    summary: 'Updated Holdover Time (HOT) calculation tables and two-step Type IV application guidelines awaiting final approval.'
  }
];

// 10. SYSTEM TELEMETRY INDICATORS
export const TELEMETRY_STREAM_DATA: IndicatorRecordItem[] = [
  {
    id: 'STRM-ACARS-01',
    title: 'ACARS Telemetry & Flight Position Downlink Feed',
    category: 'Real-time Ingestion',
    severity: 'INFO',
    status: '820 MSGS / HR',
    statusBadgeVariant: 'green',
    timeInfo: 'Latency: 120ms',
    assignedTo: 'Integration Hub',
    targetTab: 'integration_attempts',
    targetId: 'STRM-ACARS-01',
    summary: 'Live ACARS messages from 42 airborne aircraft parsed and ingested into safety telemetry cache.'
  },
  {
    id: 'STRM-FDM-02',
    title: 'Flight Data Monitoring (FDM) Auto-Triage Engine',
    category: 'Data Pipeline',
    severity: 'INFO',
    status: '340 MSGS / HR',
    statusBadgeVariant: 'green',
    timeInfo: 'Latency: 280ms',
    assignedTo: 'Integration Hub',
    targetTab: 'integration_attempts',
    targetId: 'STRM-FDM-02',
    summary: 'Post-flight QAR and WQAR optical downloads processed for automated flight parameter exceedance triggers.'
  },
  {
    id: 'STRM-METAR-03',
    title: 'Airport Weather METAR / TAF Synchronizer',
    category: 'External API Feed',
    severity: 'INFO',
    status: '240 MSGS / HR',
    statusBadgeVariant: 'green',
    timeInfo: 'Latency: 450ms',
    assignedTo: 'Integration Hub',
    targetTab: 'integration_attempts',
    targetId: 'STRM-METAR-03',
    summary: 'Automated 15-minute weather updates for 180 network airports synchronized into hazard calculation service.'
  }
];

export const TELEMETRY_DLQ_DATA: IndicatorRecordItem[] = [
  {
    id: 'DLQ-STATUS-00',
    title: 'Zero Dead-Letter Queue Messages (Buffer Clear)',
    category: 'Integration Health',
    severity: 'INFO',
    status: '0 DLQ MESSAGES',
    statusBadgeVariant: 'gray',
    timeInfo: 'Checked: Just now',
    assignedTo: 'Integration Hub',
    targetTab: 'integration_attempts',
    targetId: 'DLQ-STATUS-00',
    summary: 'All outbox transactions and inbound API payloads successfully validated, mapped, and committed.'
  }
];
